import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { IAdditionalInfoProps } from '@app/dashboard/types/innerLayout/payment_links/single_quick_link';
import {
  ADDITIONAL_INFO,
  ADDITIONAL_INFO_KEYS,
  ALL_CUSTOMER_INFO_FIELDS,
  CUSTOMER_DETAILS_TYPES,
  SINGLE_PAYMENTS_FIELDS,
} from '@app/dashboard/constants/payment-links/single-payments';

import MandatoryItemsSelector from './components/MandatoryItemsSelector';
import AdditionalInfoToggles from './components/AdditionalInfoToggles';

import styles from './additional-info.module.scss';

const AdditionalInfo = ({
  shouldFillData,
  setFields,
  fields,
  customerDetailsType,
}: IAdditionalInfoProps) => {
  const { t } = useTranslation();

  const isShouldFilledToggleOff = useMemo(() => {
    return (
      customerDetailsType === CUSTOMER_DETAILS_TYPES.ONE && !shouldFillData
    );
  }, [customerDetailsType, shouldFillData]);

  const additionalInfoOptions = useMemo(() => {
    const options = ADDITIONAL_INFO(t);

    if (isShouldFilledToggleOff) {
      return options.filter(
        (option) => option.id === ADDITIONAL_INFO_KEYS.SHIPPING_ADDRESS
      );
    }

    return options;
  }, [isShouldFilledToggleOff, t]);

  useEffect(() => {
    setFields((prev: any) => {
      const updatedFields = { ...prev };

      ALL_CUSTOMER_INFO_FIELDS.forEach((field) => {
        delete updatedFields[field];
      });

      additionalInfoOptions.forEach((item) => {
        delete updatedFields[item.value];
      });

      delete updatedFields[SINGLE_PAYMENTS_FIELDS.ALLOWED_COUNTRIES];

      updatedFields[SINGLE_PAYMENTS_FIELDS.COLLECT_PHONE_NUMBER] = {
        value: true,
      };

      return updatedFields;
    });
  }, [customerDetailsType]);

  return (
    <div className={styles['additional-info']}>
      {!isShouldFilledToggleOff && (
        <MandatoryItemsSelector
          setFields={setFields}
          customerDetailsType={customerDetailsType}
        />
      )}
      <div className={styles['additional-info__additional-options']}>
        <p className={styles['additional-info__additional-options-title']}>
          {t(
            'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.additionalInfo'
          )}
        </p>
        <AdditionalInfoToggles
          fields={fields}
          setFields={setFields}
          isShouldFilledToggleOff={isShouldFilledToggleOff}
          customerDetailsType={customerDetailsType}
        />
      </div>
    </div>
  );
};

export default memo(AdditionalInfo);
